import type { ApiConfig, CustomFieldsData } from "@smeetz/custom-field-common";
import { CustomFieldDataProvider } from "@smeetz/custom-field-common";
import { LineType } from "@smeetz/orders";
import { useOrderStore } from "@smeetz/order-store";

import { getProductCategoriesWithFilteredPrices } from "@/dexie/product-category";

type OrderLineCustomFieldsData = CustomFieldsData & {
  productName: string;
  orderLineId: string;
};

/**
 * Custom field data provider for overriding custom field service methods.
 *
 * @extends CustomFieldDataProvider
 * @param {ApiConfig} apiConfig - The API configuration object.
 */
export class CustomFieldProvider extends CustomFieldDataProvider {
  constructor(apiConfig: ApiConfig) {
    super(apiConfig);
  }

  /**
   * Fetch custom fields for a specific order.
   *
   * @param {string} orderId - The ID of the order to fetch custom fields for.
   * @returns {Promise<CustomFieldsData[]>} - A promise that resolves to an array of custom fields data.
   */
  async fetchOrderCustomFields(orderId: string): Promise<CustomFieldsData[]> {
    const orderStore = useOrderStore();
    const orders = orderStore.orders;

    // Find the order by ID
    const order = Object.values(orders).find((order) => order.id === orderId);

    if (!order) {
      throw new Error(`No order found with ID: ${orderId}`);
    }

    const allSessions = [order.orderSession, ...order.previousSessions];

    const relevantOrderLines = allSessions
      .flatMap((session) => session.orderLines ?? [])
      .filter((line) => line.lineType !== LineType.CancelLine);

    if (!relevantOrderLines.length) {
      return [];
    }

    const priceIds = [
      ...new Set(
        relevantOrderLines
          .map((line) => line.priceId)
          .filter(
            (priceId): priceId is string =>
              priceId !== undefined && priceId !== null
          )
      ),
    ];

    if (priceIds.length === 0) {
      return [];
    }

    const productCategories = await getProductCategoriesWithFilteredPrices(
      priceIds
    );

    const priceIdToProductCategory = new Map<
      string,
      (typeof productCategories)[number]
    >();

    // Map price uuid → numeric id for matching against ICustomField.prices
    const priceUuidToNumericId = new Map<string, number>();

    for (const productCategory of productCategories) {
      for (const price of productCategory.prices ?? []) {
        if (!priceIdToProductCategory.has(price.uuid)) {
          priceIdToProductCategory.set(price.uuid, productCategory);
        }
        priceUuidToNumericId.set(price.uuid, price.id);
      }
    }

    const customFieldsData = relevantOrderLines
      .map<OrderLineCustomFieldsData | null>((line) => {
        if (!line.priceId || !line.id) {
          return null;
        }

        const productCategory = priceIdToProductCategory.get(line.priceId);

        if (!productCategory) {
          return null;
        }

        const numericPriceId = priceUuidToNumericId.get(line.priceId);

        // Filter custom fields by price association:
        // if field.prices is empty/undefined → field applies to all prices
        // if field.prices is non-empty → field applies only to listed prices
        const isFieldForPrice = (field: { prices?: number[] }) =>
          !field.prices?.length ||
          numericPriceId === undefined ||
          field.prices.includes(numericPriceId);

        const bookingFields = (
          productCategory.product?.customBookingFields || []
        ).filter(isFieldForPrice);
        const ticketFields = (
          productCategory.product?.customTicketFields || []
        ).filter(isFieldForPrice);

        if (!bookingFields.length && !ticketFields.length) {
          return null;
        }

        return {
          productId: productCategory.product.id,
          productName: productCategory.name,
          orderLineId: line.id,
          customFields: [
            ...bookingFields.map((field) => ({
              fieldId: Number(field.id),
              label: field.name,
              name: field.name,
              questionType: field.questionType,
              informationType: field.informationType,
              validation: Boolean(field.validation),
              list: field.list || [],
              position: field.position,
              answerExample: field.answerExample || "",
              visibleFor: field.visibleFor,
              canceled: Boolean(field.cancelled),
              kind: 1,
            })),
            ...ticketFields.map((field) => ({
              fieldId: Number(field.id),
              label: field.name,
              name: field.name,
              questionType: field.questionType,
              informationType: field.informationType,
              validation: Boolean(field.validation),
              list: field.list || [],
              position: field.position,
              answerExample: field.answerExample || "",
              visibleFor: field.visibleFor,
              canceled: Boolean(field.cancelled),
              kind: 2,
            })),
          ],
        } as OrderLineCustomFieldsData;
      })
      .filter((entry): entry is OrderLineCustomFieldsData => entry !== null);

    return customFieldsData;
  }
}
